import type { QueryKey } from '@tanstack/react-query';

import { ENDPOINTS, type SearchPackageVersionsResponse } from '../../../../shared/registrySearch';
import useQuery from '../../../ReactQuery/useQuery';
import { get } from '../../../redux/api';
import type { ThirdPartyFetcher } from '../../../../shared/fetchers';

const STALE_MS = 60_000;
const SEARCH_LIMIT = 50;

const getQueryKey = (
  fetcher: ThirdPartyFetcher | undefined,
  packageName: string,
  query: string,
): QueryKey => ['registry-search', 'versions', fetcher, packageName, query];

/**
 * Fetches the version list for a given package from the registry-search facade. Used to power
 * the version dropdown in the Add Dependency drawer.
 *
 * `query` is an optional refinement substring forwarded to the backend. For fetchers with
 * native version search (e.g. GitHub git-refs) it filters server-side; for fetchers without
 * (e.g. npm) the server fetches the full list once and filters locally. Empty/omitted query
 * returns the latest capped result set.
 *
 * Disabled until `fetcher` and a non-blank `packageName` are set AND the caller signals via
 * `enabled`. The extra gate lets callers skip the lookup entirely for inputs we structurally
 * can't autocomplete (e.g. a `gitlab.com/...` git locator while only GitHub has a `RegistryServer`).
 */
export const useSearchPackageVersions = (
  fetcher: ThirdPartyFetcher | undefined,
  packageName: string,
  query: string = '',
  enabled: boolean = true,
) => {
  // Normalize at the fetch boundary: registry identifiers can't contain whitespace (the
  // registry-search validators reject it via SAFE_CHARS), so a whitespace-only package name
  // shouldn't fire a doomed lookup, and a blank/edge-padded query shouldn't be forwarded as a
  // `q` the backend would 400 on. Trimming here keeps every caller honest, not just this one.
  const trimmedPackage = packageName.trim();
  const trimmedQuery = query.trim();

  return useQuery<SearchPackageVersionsResponse>({
    queryKey: getQueryKey(fetcher, trimmedPackage, trimmedQuery),
    queryFn: () => get<SearchPackageVersionsResponse>(
      ENDPOINTS.SEARCH_PACKAGE_VERSIONS,
      false,
      {
        fetcher: fetcher ?? '',
        package: trimmedPackage,
        ...(trimmedQuery ? { q: trimmedQuery } : {}),
        limit: SEARCH_LIMIT,
      },
    ),
    enabled: enabled && !!fetcher && !!trimmedPackage,
    staleTime: STALE_MS,
  });
};
